import { useId, useState, type ReactElement } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
    IpxCredentialFieldClassification,
    type IpxCredentialFieldDescriptor,
    type RevealIpxCredentialSecretFieldResponse,
} from '@pillar-portal/api-types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { runInBackground } from '@/lib/utils';

const MASKED_SECRET_VALUE = '••••••••••••••••';
const DEFAULT_REVEAL_DISABLED_REASON =
    'You do not have permission to reveal this credential field.';

interface RevealedSecretField {
    readonly fieldName: string;
    readonly fieldValue: string;
}

export interface IpxCredentialFieldDisplayListProps {
    readonly fieldDescriptors: readonly IpxCredentialFieldDescriptor[];
    readonly publicFields: Readonly<Record<string, string>>;
    readonly canRevealSecretFields: boolean;
    readonly revealSecretFieldsDisabledReason?: string;
    readonly onRevealSecretField: (
        fieldName: string
    ) => Promise<RevealIpxCredentialSecretFieldResponse>;
}

function getFieldInputValue(params: {
    readonly descriptor: IpxCredentialFieldDescriptor;
    readonly publicFields: Readonly<Record<string, string>>;
    readonly revealedSecretField: RevealedSecretField | null;
}): string {
    if (
        params.descriptor.field_classification ===
        IpxCredentialFieldClassification.PUBLIC
    ) {
        return params.publicFields[params.descriptor.field_name] ?? '';
    }
    if (
        params.revealedSecretField?.fieldName === params.descriptor.field_name
    ) {
        return params.revealedSecretField.fieldValue;
    }
    return MASKED_SECRET_VALUE;
}

function getRevealErrorMessage(error: unknown): string {
    return error instanceof Error
        ? error.message
        : 'Unable to reveal credential field';
}

export function IpxCredentialFieldDisplayList(
    props: IpxCredentialFieldDisplayListProps
): ReactElement {
    const componentId: string = useId();
    const [revealedSecretField, setRevealedSecretField] =
        useState<RevealedSecretField | null>(null);
    const [revealingFieldName, setRevealingFieldName] = useState<string | null>(
        null
    );
    const [revealErrorMessage, setRevealErrorMessage] = useState<string | null>(
        null
    );

    const revealSecretField = (fieldName: string): void => {
        if (!props.canRevealSecretFields) {
            return;
        }
        setRevealErrorMessage(null);
        setRevealedSecretField(null);
        setRevealingFieldName(fieldName);
        runInBackground(
            props
                .onRevealSecretField(fieldName)
                .then((response) => {
                    setRevealedSecretField({
                        fieldName: response.field_name,
                        fieldValue: response.field_value,
                    });
                })
                .catch((error: unknown) => {
                    setRevealErrorMessage(getRevealErrorMessage(error));
                })
                .finally(() => {
                    setRevealingFieldName(null);
                }),
            () => undefined
        );
    };

    return (
        <div className="space-y-3">
            {revealErrorMessage !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>{revealErrorMessage}</AlertDescription>
                </Alert>
            ) : null}
            {props.fieldDescriptors.map((descriptor) => {
                const isSecretField: boolean =
                    descriptor.field_classification ===
                    IpxCredentialFieldClassification.SECRET;
                const isRevealedSecretField: boolean =
                    revealedSecretField?.fieldName === descriptor.field_name;
                const isRevealingThisField: boolean =
                    revealingFieldName === descriptor.field_name;
                const inputId: string = `${componentId}-${descriptor.field_name}`;
                const revealDisabledReason: string | undefined =
                    props.canRevealSecretFields
                        ? undefined
                        : (props.revealSecretFieldsDisabledReason ??
                          DEFAULT_REVEAL_DISABLED_REASON);
                const revealButtonTitle: string | undefined =
                    revealDisabledReason ??
                    (isRevealingThisField
                        ? 'Revealing credential field...'
                        : isRevealedSecretField
                          ? `Hide ${descriptor.display.label}`
                          : `Reveal ${descriptor.display.label}`);
                return (
                    <div key={descriptor.field_name} className="space-y-2">
                        <Label htmlFor={inputId}>
                            {descriptor.display.label}
                        </Label>
                        {descriptor.rotatable === true ? (
                            <div className="text-xs text-muted-foreground">
                                Rotatable
                            </div>
                        ) : null}
                        {descriptor.confirmation_field_name === undefined ? null : (
                            <div className="text-xs text-muted-foreground">
                                Confirms {descriptor.confirmation_field_name}
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <Input
                                id={inputId}
                                readOnly
                                value={getFieldInputValue({
                                    descriptor,
                                    publicFields: props.publicFields,
                                    revealedSecretField,
                                })}
                            />
                            {isSecretField ? (
                                <span
                                    className="inline-flex"
                                    title={revealButtonTitle}
                                >
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        disabled={
                                            !props.canRevealSecretFields ||
                                            isRevealingThisField
                                        }
                                        title={revealButtonTitle}
                                        aria-label={
                                            isRevealedSecretField
                                                ? `Hide ${descriptor.display.label}`
                                                : `Reveal ${descriptor.display.label}`
                                        }
                                        onClick={() => {
                                            if (!props.canRevealSecretFields) {
                                                return;
                                            }
                                            if (isRevealedSecretField) {
                                                setRevealedSecretField(null);
                                                return;
                                            }
                                            revealSecretField(
                                                descriptor.field_name
                                            );
                                        }}
                                    >
                                        {isRevealedSecretField ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                </span>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
